import { create } from 'zustand';

import { getAuth } from '@services/identity.service';
import { logsToAwsCloudWatch } from '@services/logger.service';
import { LinkLibraryReducer, defaultState } from '@components/Features/LinkLibraryReducer';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRecord = Record<string, any>;

const DEFAULT_ID = 'a17fa8c2-2eaf-407e-9d7c-ca714298a513';

export interface StreamState {
  // Connection timing (used for CloudWatch logging)
  connectionTime: { start: number | null; end: number | null; platform: string | null };
  setConnectionTime: (patch: Partial<StreamState['connectionTime']>) => void;

  // Firestore unsubscribe handles
  firestoreUnsub: AnyRecord;
  setFirestoreUnsub: (unsub: AnyRecord) => void;

  // Overlay comment subscriptions
  unsubOverlayComments: (() => void) | null;
  setUnsubOverlayComments: (fn: (() => void) | null) => void;
  overlayCommentsTimerId: string;
  setOverlayCommentsTimerId: (id: string) => void;

  // Questions
  newQuestion: unknown;
  setNewQuestion: (q: unknown) => void;

  // Map
  mapBox: string;
  setMapBox: (box: string) => void;
  hiddenData: AnyRecord;
  setHiddenData: (data: AnyRecord) => void;
  particularMap: AnyRecord;
  setParticularMap: (map: AnyRecord) => void;

  // Voting
  oneUserOneVote: boolean;
  setOneUserOneVote: (v: boolean) => void;

  // Dismiss / error
  overrideDismissStatus: boolean;
  setOverrideDismissStatus: (v: boolean) => void;
  showApiError: boolean;
  setShowApiError: (v: boolean) => void;
  errMessage: string;
  setErrMessage: (msg: string) => void;

  // Feature data
  featureQuestion: { id: string; type: string; title: string; options: unknown[] };
  setFeatureQuestion: (q: StreamState['featureQuestion']) => void;
  resultPollData: { id: string; comments: unknown[]; title: string; type: string };
  setResultPollData: (data: StreamState['resultPollData']) => void;
  resultMapData: { id: string; title: string; features: unknown[] };
  setResultMapData: (data: StreamState['resultMapData']) => void;
  resultWordCloudData: { id: string; comments: unknown[]; title: string };
  setResultWordCloudData: (data: StreamState['resultWordCloudData']) => void;
  resultWheelData: { id: string; comments: unknown[]; title: string };
  setResultWheelData: (data: StreamState['resultWheelData']) => void;
  resultTtData: AnyRecord;
  setResultTtData: (data: AnyRecord) => void;
  nextQuestion: unknown[];
  setNextQuestion: (q: unknown[]) => void;
  featureId: string;
  setFeatureId: (id: string) => void;
  showUserName: boolean;
  setShowUserName: (value: boolean) => void;
  featureSettings: AnyRecord;
  setFeatureSettings: (s: AnyRecord) => void;
  featureBrandColours: AnyRecord;
  setFeatureBrandColours: (c: AnyRecord) => void;

  // User guide
  showUserGuide: boolean;
  setShowUserGuide: (v: boolean) => void;

  // Interaction
  interactionQuestion: AnyRecord;
  setInteractionQuestion: (q: AnyRecord) => void;

  // Selection
  selectedQuestion: unknown;
  setSelectedQuestion: (q: unknown) => void;

  // Routing
  preventRoute: string | undefined;
  setPreventRoute: (route: string | undefined) => void;
  route: string | undefined;
  setRoute: (route: string | undefined) => void;

  // Polling interval

  // Pagination
  questionPage: number;
  setQuestionPage: (page: number) => void;

  // Map data cache
  prevMapDataById: AnyRecord;
  setPrevMapDataById: (data: AnyRecord) => void;

  // Livestream ID
  liveStreamId: string | null;
  setLivestreamId: (id: string | null) => void;

  // Session history
  sessionHistory: AnyRecord;
  setSessionHistory: (history: AnyRecord) => void;

  // Presentation editing
  isCreateOrEditPresentation: boolean;
  setIsCreateOrEditPresentation: (v: boolean) => void;
  rosPayload: AnyRecord;
  setRosPayload: (payload: AnyRecord) => void;

  /**
   * showOverlay is intentionally non-reactive (mirrors the original useRef).
   * Mutating `.current` does NOT trigger re-renders in subscribers.
   */
  showOverlay: { current: boolean };
  setShowOverlay: (v: boolean) => void;

  // Presentation live flag (set by ros.js page on mount/unmount)
  isPresentationLive: boolean;
  setIsPresentationLive: (v: boolean) => void;

  // Map location filter
  selectedLocation: string;
  setSelectedLocation: (loc: string) => void;

  // Link library (replaces useReducer pattern)
  linkLibraryState: typeof defaultState;
  dispatchLinkLibrary: (action: Parameters<typeof LinkLibraryReducer>[1]) => void;
}

export const useStreamStore = create<StreamState>()((set, get) => ({
  connectionTime: { start: null, end: null, platform: null },
  setConnectionTime: (patch) => {
    const next = { ...get().connectionTime, ...patch };
    set({ connectionTime: next });
    if (next.end !== null) {
      const { start, end, platform } = next;
      const duration = ((end ?? 0) - (start ?? 0)) / 1000;
      const auth = getAuth();
      logsToAwsCloudWatch({
        logMessage: `Time taken for ${platform} bot to connect: ${duration} s`,
        logData: { userId: (auth as AnyRecord)?.id || '' },
      });
    }
  },

  firestoreUnsub: {},
  setFirestoreUnsub: (firestoreUnsub) => set({ firestoreUnsub }),

  unsubOverlayComments: null,
  setUnsubOverlayComments: (unsubOverlayComments) => set({ unsubOverlayComments }),
  overlayCommentsTimerId: '',
  setOverlayCommentsTimerId: (overlayCommentsTimerId) => set({ overlayCommentsTimerId }),

  newQuestion: null,
  setNewQuestion: (newQuestion) => set({ newQuestion }),

  mapBox: '',
  setMapBox: (mapBox) => set({ mapBox }),
  hiddenData: {},
  setHiddenData: (hiddenData) => set({ hiddenData }),
  particularMap: {},
  setParticularMap: (particularMap) => set({ particularMap }),

  oneUserOneVote: false,
  setOneUserOneVote: (oneUserOneVote) => set({ oneUserOneVote }),

  overrideDismissStatus: false,
  setOverrideDismissStatus: (overrideDismissStatus) => set({ overrideDismissStatus }),
  showApiError: false,
  setShowApiError: (showApiError) => set({ showApiError }),
  errMessage: 'Something went wrong. Please try again.',
  setErrMessage: (errMessage) => set({ errMessage }),

  featureQuestion: { id: DEFAULT_ID, type: '', title: '', options: [] },
  setFeatureQuestion: (featureQuestion) => set({ featureQuestion }),
  resultPollData: { id: DEFAULT_ID, comments: [], title: '', type: '' },
  setResultPollData: (resultPollData) => set({ resultPollData }),
  resultMapData: { id: DEFAULT_ID, title: '', features: [] },
  setResultMapData: (resultMapData) => set({ resultMapData }),
  resultWordCloudData: { id: DEFAULT_ID, comments: [], title: '' },
  setResultWordCloudData: (resultWordCloudData) => set({ resultWordCloudData }),
  resultWheelData: { id: DEFAULT_ID, comments: [], title: '' },
  setResultWheelData: (resultWheelData) => set({ resultWheelData }),
  resultTtData: {},
  setResultTtData: (resultTtData) => set({ resultTtData }),
  nextQuestion: [],
  setNextQuestion: (nextQuestion) => set({ nextQuestion }),
  featureId: '',
  setFeatureId: (featureId) => set({ featureId }),
  showUserName: false,
  setShowUserName: (showUserName) => set({ showUserName }),
  featureSettings: {},
  setFeatureSettings: (featureSettings) => set({ featureSettings }),
  featureBrandColours: {},
  setFeatureBrandColours: (featureBrandColours) => set({ featureBrandColours }),

  showUserGuide: false,
  setShowUserGuide: (showUserGuide) => set({ showUserGuide }),

  interactionQuestion: {},
  setInteractionQuestion: (interactionQuestion) => set({ interactionQuestion }),

  selectedQuestion: null,
  setSelectedQuestion: (selectedQuestion) => set({ selectedQuestion }),

  preventRoute: undefined,
  setPreventRoute: (preventRoute) => set({ preventRoute }),
  route: undefined,
  setRoute: (route) => set({ route }),

  questionPage: 0,
  setQuestionPage: (questionPage) => set({ questionPage }),

  prevMapDataById: {},
  setPrevMapDataById: (prevMapDataById) => set({ prevMapDataById }),

  liveStreamId: null,
  setLivestreamId: (liveStreamId) => set({ liveStreamId }),

  sessionHistory: {},
  setSessionHistory: (sessionHistory) => set({ sessionHistory }),

  isCreateOrEditPresentation: false,
  setIsCreateOrEditPresentation: (isCreateOrEditPresentation) => set({ isCreateOrEditPresentation }),
  rosPayload: {},
  setRosPayload: (rosPayload) => set({ rosPayload }),

  // Non-reactive ref — setShowOverlay mutates `.current` without calling set()
  showOverlay: { current: true },
  setShowOverlay: (value) => {
    get().showOverlay.current = value;
  },

  isPresentationLive: false,
  setIsPresentationLive: (isPresentationLive) => set({ isPresentationLive }),

  selectedLocation: 'World',
  setSelectedLocation: (selectedLocation) => set({ selectedLocation }),

  linkLibraryState: { ...defaultState },
  dispatchLinkLibrary: (action) => {
    set((s) => ({ linkLibraryState: LinkLibraryReducer(s.linkLibraryState, action) }));
  },
}));

// ---------------------------------------------------------------------------
// Connection-time bridge: Zoom.js dispatches `zoom-bot-command-context` window
// events with `cmd: 'updateConnectionTime'`. The `updateBotStatus` branch is
// handled by zoomBotStore (Zoom-bot specific).
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  window.addEventListener('zoom-bot-command-context', (e: Event) => {
    const { cmd, ...detail } = (e as CustomEvent).detail;
    if (cmd === 'updateConnectionTime') {
      useStreamStore.getState().setConnectionTime(detail);
    }
  });
}
